import {
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Paper,
  TextField
} from '@material-ui/core'

import { getTranslate } from '../../localization'
import SelectionFilters, { Option } from './SelectionFilters'
import useStyles from './styles/InventoryHealthFilter.styles'
import { QueryFilters } from './useInventoryHealthQueryFilters'

type Props = {
  open: boolean
  filters: QueryFilters
  set: <K extends keyof QueryFilters>(key: K, value: QueryFilters[K]) => void
  channelOptions: Option[]
  onApply: () => void
  onReset: () => void
  canApply: boolean
  applyLabel: string
}

/**
 * The query filters — what gets FETCHED. Per portal-ux-conventions §3, 2+
 * fields belong in a collapsed panel behind the header's Filter toggle, and
 * they apply on Apply rather than on change.
 *
 * Applying on change would be actively wrong here rather than merely
 * unconventional: every apply is a PMS-backed query, and the channel picker is
 * populated FROM the previous response, so a mid-edit re-query would repopulate
 * the very field being edited.
 *
 * Narrowing what is already on screen is a different act with a different cost,
 * and lives in InventoryHealthColumnFilters.
 */
export default function InventoryHealthFilter({
  open,
  filters,
  set,
  channelOptions,
  onApply,
  onReset,
  canApply,
  applyLabel
}: Props) {
  const classes = useStyles()

  return (
    <Collapse in={open}>
      <Paper className={classes.panel}>
        <div className={classes.fields}>
          <SelectionFilters
            accommodation={filters.accommodation}
            setAccommodation={(value) => set('accommodation', value)}
            provider={filters.provider}
            setProvider={(value) => set('provider', value)}
            channel={filters.channel}
            setChannel={(value) => set('channel', value)}
            channelOptions={channelOptions}
          />
          <TextField
            label={getTranslate('From')}
            type="date"
            value={filters.from}
            onChange={(event) => set('from', event.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={getTranslate('To')}
            type="date"
            value={filters.to}
            onChange={(event) => set('to', event.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div className={classes.toggles}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.differencesOnly}
                onChange={(event) =>
                  set('differencesOnly', event.target.checked)
                }
              />
            }
            label={getTranslate('Show only rows where layers disagree')}
          />
        </div>

        <div className={classes.actions}>
          {/* Reset clears the fields and does NOT re-query — one click, one meaning. */}
          <Button onClick={onReset}>{getTranslate('Reset')}</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canApply}
            onClick={onApply}>
            {applyLabel}
          </Button>
        </div>
      </Paper>
    </Collapse>
  )
}
